/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { IAttachment, IMessage, ITicket } from '../../types/tickets';

interface ITicketsMeta {
  perPage: string;
  total: number;
  currentPage: number;
  orderByDirection: 'desc' | 'asc';
  hash: null | string;
}

interface ITicketsState {
  list: ITicket[];
  listMeta: ITicketsMeta;
  loading: {
    list: boolean;
    selectedTicket: boolean;
    attachments: boolean;
    createTicket: boolean;
    fetchUnreadTickets: boolean;
  };
  selectedTicket: null | ITicket;
  replyAttachments: IAttachment[];
  unreadMessages: IMessage[];
}

const initialState = {
  list: [],
  listMeta: { perPage: '15', total: 0, orderByDirection: 'desc', currentPage: 1, hash: null },
  loading: {
    list: false,
    selectedTicket: false,
    attachments: false,
    createTicket: false,
    fetchUnreadTickets: false,
  },
  selectedTicket: null,
  replyAttachments: [],
  unreadMessages: [],
} as ITicketsState;

const ticketsSlice = createSlice({
  name: 'tickets',
  initialState,
  reducers: {
    setTickets(state, action: PayloadAction<ITicket[]>) {
      // eslint-disable-next-line no-param-reassign
      state.list = action.payload;
    },
    updateTicketsList(state, action: PayloadAction<ITicket>) {
      state.list = state.list.map((ticket) => ({ ...ticket, ...action.payload }));
    },
    setTicketsMeta(state, action: PayloadAction<Partial<ITicketsMeta>>) {
      state.listMeta = { ...state.listMeta, ...action.payload };
    },
    setTicketsLoading(state, action: PayloadAction<boolean>) {
      state.loading.list = action.payload;
    },
    setSelectedTicketLoading(state, action: PayloadAction<boolean>) {
      state.loading.selectedTicket = action.payload;
    },
    setSelectedTicket(state, action: PayloadAction<ITicket>) {
      state.selectedTicket = action.payload;
    },
    updateSelectedTicket(state, action: PayloadAction<Partial<ITicket>>) {
      state.selectedTicket = { ...state.selectedTicket, ...action.payload };
    },
    setLoading(state, action: PayloadAction<{ key: string; value: boolean }>) {
      //@ts-expect-error
      state.loading[action.payload.key] = action.payload.value;
    },
    setReplyAttachments(state, action: PayloadAction<IAttachment>) {
      state.replyAttachments = [...state.replyAttachments, action.payload];
    },
    deleteReplyAttachment(state, action: PayloadAction<number>) {
      state.replyAttachments = state.replyAttachments.filter(({ id }) => id !== action.payload);
    },
    addMessageToSelectedTicket(state, action: PayloadAction<IMessage>) {
      state.selectedTicket.messages.push(action.payload);
    },
    clearReplyAttachments(state) {
      state.replyAttachments = [];
    },
    addTicketToList(state, action: PayloadAction<ITicket>) {
      const isTicketExist = state.list.some(({ id }) => id === action.payload.id);

      if (!isTicketExist) {
        state.list.unshift(action.payload);
      }
    },
    addMessageToTicketList(state, action: PayloadAction<IMessage>) {
      state.list.forEach((ticket) => {
        if (ticket.id === action.payload.ticketId) {
          ticket.lastMessage = action.payload;
          ticket.unread = 1;
        }
      });

      if (!state.selectedTicket) {
        state.unreadMessages.push(action.payload);
      }
    },
    setUnreadTicketMessages(state, action: PayloadAction<IMessage[]>) {
      state.unreadMessages = action.payload;
    },
    removeMessagesFromUnread(state, action: PayloadAction<number>) {
      state.unreadMessages = state.unreadMessages.filter(
        ({ ticketId }) => ticketId !== action.payload,
      );
    },
    resetState: (state) => ({
      ...initialState,
      unreadMessages: state.unreadMessages,
      listMeta: {
        ...initialState.listMeta,
        hash: state.listMeta.hash,
      },
    }),
  },
});

export const {
  setTickets,
  setTicketsMeta,
  setTicketsLoading,
  setSelectedTicketLoading,
  setSelectedTicket,
  updateSelectedTicket,
  setLoading,
  setReplyAttachments,
  deleteReplyAttachment,
  addMessageToSelectedTicket,
  clearReplyAttachments,
  resetState,
  addTicketToList,
  addMessageToTicketList,
  setUnreadTicketMessages,
  removeMessagesFromUnread,
  updateTicketsList,
} = ticketsSlice.actions;

export default ticketsSlice.reducer;
